import { useState } from 'react'
import { useTranslation } from '@/i18n'
import { cn } from '@/lib/utils'
import { IntuiButton } from '@/components/intueri/IntuiButton'
import { IntuiTextarea } from '@/components/intueri/IntuiTextarea'
import type { Ask } from '../v4/contracts'
import { typedText } from '../v4/conversation-text'

export type AnswerAsk = (resumeToken: string, values: Record<string, string>) => Promise<void>
export type SendFile = (file: File) => Promise<void>

/**
 * Picking a method file sends it at once: one click, no second button. While the model reads it,
 * the label says so; an error shows next to it. Ported from the v4 page's Conversation.
 */
export function MethodFilePick({ label, enabled, onFile }: { label: string; enabled: boolean; onFile: SendFile }) {
  const { t } = useTranslation()
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const pick = async (file: File | undefined) => {
    if (!file) return
    setSending(true)
    setError('')
    try {
      await onFile(file)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setSending(false)
    }
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <label
        className={cn(
          'cursor-pointer rounded-lg border border-border px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted',
          (!enabled || sending) && 'cursor-not-allowed opacity-60 hover:bg-transparent'
        )}
      >
        {sending ? t('workflow:ask.reading_file', { defaultValue: 'Reading your file…' }) : label}
        <input
          type="file"
          accept=".pdf,.md,.markdown,.txt"
          hidden
          disabled={!enabled || sending}
          onChange={(e) => {
            void pick(e.target.files?.[0])
            e.target.value = ''
          }}
        />
      </label>
      {error && <span className="text-xs text-red-600 dark:text-red-400">{error}</span>}
    </span>
  )
}

/**
 * One of the engine's asks in the chat: each slot with its reason and what was tried, the options
 * as chips (the default marked), a text box where the engine takes free text, and for a method ask
 * with no options the "answer several questions together" box. Answer sends the values with the
 * ask's resume token; Use defaults sends none. Closed once the engine moved on.
 */
export default function AskCard({
  ask,
  open,
  busy,
  onAnswer,
  onFile,
}: {
  ask: Ask
  open: boolean
  busy: boolean
  onAnswer: AnswerAsk
  onFile: SendFile
}) {
  const { t } = useTranslation()
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(ask.slots.filter((s) => s.safeDefault !== null).map((s) => [s.slot, s.safeDefault as string]))
  )
  const [message, setMessage] = useState('')
  const [sending, setSending] = useState(false)
  const interview = ask.slots.some((s) => s.kind === 'method' && s.options.length === 0)
  const enabled = open && !sending && !busy
  const submit = async (answers: Record<string, string>) => {
    setSending(true)
    try {
      await onAnswer(ask.resumeToken, answers)
    } finally {
      setSending(false)
    }
  }
  const title = ask.stepId
    ? t('workflow:ask.mid_run_title', { defaultValue: 'Question from step {{step}}', step: ask.stepId })
    : t('workflow:ask.title', { defaultValue: 'The engine has {{count}} question(s)', count: ask.slots.length })
  const complete = interview
    ? !!message.trim() || Object.values(values).some((v) => v.trim())
    : ask.slots.every((s) => values[s.slot]?.trim())

  return (
    <div className={cn('rounded-xl border border-border bg-background p-3.5 dark:bg-dark-card', !open && 'opacity-70')}>
      <p className="text-sm font-medium text-foreground">{title}</p>
      {ask.slots.map((s) => (
        <div key={s.slot} className="mt-3 flex flex-col gap-1.5">
          <span className="text-sm text-foreground">
            <b className="font-mono text-xs">{s.slot}</b>{' '}
            <span className="text-xs text-muted-foreground">
              {s.reason}
              {s.attempted.length
                ? ` ${t('workflow:ask.tried', { defaultValue: 'Tried: {{list}}.', list: s.attempted.join(', ') })}`
                : ''}
            </span>
          </span>
          {s.options.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {s.options.map((o) =>
                s.slot === 'method' && o.value === 'send_file' ? (
                  <MethodFilePick key={o.value} label={o.label} enabled={enabled} onFile={onFile} />
                ) : (
                  <button
                    key={o.value}
                    type="button"
                    disabled={!enabled}
                    aria-pressed={values[s.slot] === o.value}
                    onClick={() => setValues({ ...values, [s.slot]: o.value })}
                    className={cn(
                      'rounded-lg border px-3 py-2 text-left text-sm font-medium transition-colors disabled:opacity-60',
                      values[s.slot] === o.value
                        ? 'border-cyan-400 bg-cyan-50 text-foreground dark:border-cyan-700 dark:bg-cyan-950/40'
                        : 'border-border text-foreground hover:bg-muted disabled:hover:bg-transparent'
                    )}
                  >
                    {o.label}
                    {o.label.toLowerCase().includes(o.value.toLowerCase()) ? '' : (
                      <span className="ml-1 font-mono text-xs text-muted-foreground">= {o.value}</span>
                    )}
                    {o.value === s.safeDefault ? ` ${t('workflow:ask.default_mark', { defaultValue: '(default)' })}` : ''}
                  </button>
                )
              )}
            </div>
          )}
          {(s.options.length === 0 || s.kind === 'binding') &&
            (s.kind === 'method' ? (
              <IntuiTextarea
                aria-label={s.slot}
                minRows={3}
                disabled={!enabled}
                value={values[s.slot] ?? ''}
                onChange={(e) => setValues({ ...values, [s.slot]: e.target.value })}
                placeholder={t('workflow:ask.write_answer', { defaultValue: 'Write your answer…' })}
              />
            ) : (
              <input
                aria-label={s.slot}
                disabled={!enabled}
                value={typedText(s, values[s.slot])}
                onChange={(e) => setValues({ ...values, [s.slot]: e.target.value })}
                placeholder={t('workflow:ask.write_answer', { defaultValue: 'Write your answer…' })}
                className="rounded-lg border border-border bg-transparent px-2.5 py-1.5 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-cyan-400"
              />
            ))}
        </div>
      ))}
      {interview && (
        <label className="mt-3 flex flex-col gap-1 text-sm text-foreground">
          {t('workflow:ask.interview_title', { defaultValue: 'Answer several questions together' })}
          <IntuiTextarea
            minRows={5}
            disabled={!enabled}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={t('workflow:ask.interview_placeholder', {
              defaultValue: 'Write your answers in your own words. The engine builds the ordered method.',
            })}
          />
          <span className="text-xs text-muted-foreground">
            {t('workflow:ask.interview_note', {
              defaultValue: 'Every question is optional. Answer what applies; the engine plans from what you give.',
            })}
          </span>
        </label>
      )}
      {open ? (
        <div className="mt-3 flex items-center gap-2">
          <IntuiButton size="sm" disabled={!complete || sending || busy} onClick={() => void submit(message.trim() ? { ...values, message } : values)}>
            {sending ? t('workflow:ask.sending', { defaultValue: 'Sending…' }) : t('workflow:ask.answer', { defaultValue: 'Answer' })}
          </IntuiButton>
          <IntuiButton
            size="sm"
            variant="outline"
            disabled={sending || busy || ask.slots.some((s) => s.safeDefault === null)}
            onClick={() => void submit({})}
          >
            {t('workflow:ask.use_defaults', { defaultValue: 'Use defaults' })}
          </IntuiButton>
        </div>
      ) : (
        <p className="mt-3 text-xs text-muted-foreground">{t('workflow:ask.closed', { defaultValue: 'Closed.' })}</p>
      )}
    </div>
  )
}
